import { useCallback } from "react";
import type LoggingProperties from "./Logging.properties.ts";
import {
  type LoggingLoggingInput,
  LoggingType,
} from "../../generated/fresh-guard-api";
import { LoggingContext } from "./Logging.context.tsx";
import type { LabelsType } from "./Labels.type.ts";
import type { TechnicalContentType } from "./TechnicalContent.type.ts";
import { useLoggingInsert } from "../../apis/logging/useLoggingInsert.ts";
import { lsLoad } from "../../tools/Storage.ts";
import StorageEnum from "../../enums/Storage.enum.ts";

export const LoggingProvider = (props: LoggingProperties) => {
  const { mutate } = useLoggingInsert();

  const logGeneral = useCallback(
    (
      type: LoggingType,
      content: string,
      component: string,
      uuid: string | null,
      onFinished?: () => void,
    ) => {
      const technicalContent: TechnicalContentType = {
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        screenColorDepth: window.screen.colorDepth,
        screenPixelDepth: window.screen.pixelDepth,
        origin: window.origin,
        characterSet: document.characterSet,
        contentType: document.contentType,
        language: navigator.language,
        content: content,
      };

      const loggingInput: LoggingLoggingInput = {
        type: type,
        content: JSON.stringify(technicalContent),
        // @ts-expect-error requires array
        labels: {
          app: "react",
          path: window.location.pathname,
          windowWidth: window.innerWidth.toString(),
          windowHeight: window.innerHeight.toString(),
          referrer: document.referrer,
          userAgent: navigator.userAgent,
          component: component,
          host: window.location.host,
          ...(uuid ? { uuid } : {}),
        } as LabelsType,
      };

      if (!lsLoad(StorageEnum.Jwt)) {
        return;
      }

      mutate(
        { loggingInput },
        {
          onSettled: () => {
            if (onFinished) {
              onFinished();
            }
          },
        },
      );
    },
    [mutate],
  );

  const logInfo = useCallback(
    (content: string, component: string, onFinished?: () => void) => {
      logGeneral(LoggingType.Info, content, component, null, onFinished);
    },
    [logGeneral],
  );

  const logError = useCallback(
    (
      content: string,
      component: string,
      uuid: string,
      onFinished?: () => void,
    ) => {
      logGeneral(LoggingType.Error, content, component, uuid, onFinished);
    },
    [logGeneral],
  );

  const logTrace = useCallback(
    (
      content: string,
      component: string,
      uuid: string,
      onFinished?: () => void,
    ) => {
      logGeneral(LoggingType.Trace, content, component, uuid, onFinished);
    },
    [logGeneral],
  );

  return (
    <LoggingContext.Provider
      value={{
        logInfo,
        logError,
        logTrace,
      }}
    >
      {props.children}
    </LoggingContext.Provider>
  );
};
